// Business and validation schemas
import { validate, schemas } from "../utils/validations";

import { inferChannel, internalSource, currentUrl } from "./page";

// Utilities
import timeStamp from "../utils/timestamp";
import existsGTM from "../utils/existsGTM";
import reportError from "../utils/reportError";
import withCallbacks from "../utils/withCallbacks";
import { resetDataLayer } from "../utils/dataLayer";
import { VERSION } from "../constants";

// 🚀 NUEVA INYECCIÓN DEL BRIDGE PARA WEBVIEW (CORREGIDA Y SEGURA) 🚀
(function () {
  if (typeof document === 'undefined') {
    return;
  }


  const script = document.createElement("script");


  /* Este script actúa como un "espía" del dataLayer:
    Intercepta cada push y lo reenvía a React Native si detecta el bridge de forma segura.
 */
  script.innerHTML = `
   (function() {
     window.dataLayer = window.dataLayer || [];
     var originalPush = window.dataLayer.push;


     window.dataLayer.push = function() {
       var args = Array.prototype.slice.call(arguments);
       var eventData = args[0];


       // ENVIAR HACIA LA APP (React Native):
       if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
         try {
           // Filtro para ignorar elementos HTML u objetos internos de React y evitar el error circular
           var safeJson = JSON.stringify(eventData, function(key, value) {
             if (value instanceof HTMLElement || (value && value.constructor && value.constructor.name === 'FiberNode')) {
               return undefined;
             }
             return value;
           });


           window.ReactNativeWebView.postMessage(safeJson);
         } catch (e) {
           console.error("Error sending message to RN:", e);
         }
       }


       // SEGUIR FLUJO NORMAL EN LA WEB:
       return originalPush.apply(window.dataLayer, args);
     };
   })();
 `;


  document.head.appendChild(script);
})();
// FIN DE LA INYECCIÓN


/**
 * Pagename builder
 * @param {string} channel - The channel page value
 * @param {string} family - The Family page value
 * @param {string} subFamily - The SubFamily page value
 * @param {string} action - The Action page value
 * @param {string} pageName - The name of the page
 * @return {string} Pagename
 */
function buildPagename(channel, family, subFamily, action, pageName) {
  return `${channel}|${family}|${subFamily}|${action}|${pageName}`;
}

/**
 * Builder of context payload
 * @param {string} data Data sent from the app who calls context event
 * @return {object}
 */
const buildContextPayload = (data) => {
  data.channel = inferChannel();
  const url = currentUrl();

  return {
    screen: {
      path: data.path,
      app: data.app,
      channel: data.channel,
      customer_journey: data.customerJourney,
      language: data.language,
      country: data.country,
      original_country: data.originalCountry,
      title: data.title,
      internal_source: internalSource(url),
      ...(data.trackId
        ? {
          track_id: data.trackId,
        }
        : {}),
      page_name: buildPagename(
        data.channel,
        data.family,
        data.subFamily,
        data.action,
        data.pageName
      ),
      ...(data.source
        ? {
          source: data.source,
        }
        : {}),
      ...(data.referrer
        ? {
          referrer: data.referrer,
        }
        : {}),
    },
    ...(data.customer
      ? {
        customer: {
          ...(data.customer.userID
            ? {
              user_id: data.customer.userID,
            }
            : {}),
          ...(data.customer.customerScope
            ? {
              customer_scope: data.customer.customerScope,
            }
            : {}),
          ...(data.customer.customerProfile
            ? {
              customer_profile: data.customer.customerProfile,
            }
            : {}),
          ...(data.customer.customerFFPCategory
            ? {
              customer_ffp_category: data.customer.customerFFPCategory,
            }
            : {}),
          ...(data.customer.customerGender
            ? {
              customer_gender: data.customer.customerGender,
            }
            : {}),
          ...(data.customer.customerActiveUser
            ? {
              customer_active_user: data.customer.customerActiveUser,
            }
            : {}),
          ...(data.customer.impersonatedUserID
            ? {
              impersonated_user_id: data.customer.impersonatedUserID,
            }
            : {}),
          ...(data.customer.impersonatedCustomerScope
            ? {
              impersonated_customer_scope:
                    data.customer.impersonatedCustomerScope,
            }
            : {}),
        },
      }
      : {}),
    ...(data.context
      ? {
        context: {
          ...(data.context.originCountry
            ? {
              origin_country: data.context.originCountry,
            }
            : {}),
          ...(data.context.originAirport
            ? {
              origin_airport: data.context.originAirport,
            }
            : {}),
          ...(data.context.originCity
            ? {
              origin_city: data.context.originCity,
            }
            : {}),
          ...(data.context.destinationCountry
            ? {
              destination_country: data.context.destinationCountry,
            }
            : {}),
          ...(data.context.destinationAirport
            ? {
              destination_airport: data.context.destinationAirport,
            }
            : {}),
          ...(data.context.destinationCity
            ? {
              destination_city: data.context.destinationCity,
            }
            : {}),
          ...(data.context.departureDate
            ? {
              departure_date: data.context.departureDate,
            }
            : {}),
          ...(data.context.arrivalDate
            ? {
              arrival_date: data.context.arrivalDate,
            }
            : {}),
          ...(data.context.travelType
            ? {
              travel_type: data.context.travelType,
            }
            : {}),
          ...(data.context.businessUnit
            ? {
              business_unit: data.context.businessUnit,
            }
            : {}),
          ...(data.context.cabinType
            ? {
              cabin_type: data.context.cabinType,
            }
            : {}),
          ...(data.context.adults !== undefined
            ? {
              adults: data.context.adults,
            }
            : {}),
          ...(data.context.children !== undefined
            ? {
              children: data.context.children,
            }
            : {}),
          ...(data.context.infants !== undefined
            ? {
              infants: data.context.infants,
            }
            : {}),
          ...(data.context.transactionName
            ? {
              transaction_name: data.context.transactionName,
            }
            : {}),
          ...(data.context.originalCountry
            ? {
              original_country: data.context.originalCountry,
            }
            : {}),
          ...(data.context.order
            ? {
              order: data.context.order,
            }
            : {}),
          ...(data.context.subflow
            ? {
              subflow: data.context.subflow,
            }
            : {}),
          ...(data.context.hasDiscount
            ? {
              has_discount: data.context.hasDiscount,
            }
            : {}),
          ...(data.context.measurementId
            ? {
              external_measurement_id: data.context.measurementId,
            }
            : {}),
          ...(data.context.externalMeasurement !== undefined
            ? {
              external_measurement: data.context.externalMeasurement,
            }
            : {}),
          ...(data.context.webview !== undefined
            ? {
              webview: data.context.webview,
            }
            : {}),
          ...(data.context.appNativeMeasurement !== undefined
            ? {
              app_native_measurement: data.context.appNativeMeasurement,
            }
            : {}), 
          ...(data.context.promocode !== undefined
            ? {
              promocode: data.context.promocode,
            }
            : {}),    
          ...(data.context.flightCityPath
            ? {
              flight_city_path: data.context.flightCityPath,
            }
            : {}),
          ...(data.context.insurance !== undefined
            ? {
              insurance: data.context.insurance,
            }
            : {}),
          ...(data.context.trackId
            ? {
              track_id: data.context.trackId,
            }
            : {}), 
          ...(data.context.flightNumber
            ? {
              flight_number: data.context.flightNumber,
            }
            : {}), 
          ...(data.context.ticketNumber
            ? {
              ticket_number: data.context.ticketNumber,
            }
            : {}),
          ...(data.context.hasCommercialException !== undefined
            ? {
              has_commercial_exception: data.context.hasCommercialException,
            }
            : {}),
          ...(data.context.hasFareRules !== undefined
            ? {
              has_fare_rules: data.context.hasFareRules,
            }
            : {}),  
          ...(data.context.merchantCode
            ? {
              merchant_code: data.context.merchantCode,
            }
            : {}),
          ...(data.context.stationCode
            ? {
              station_code: data.context.stationCode,
            }
            : {}),
          ...(data.context.anticipationOfPurchase !== undefined
            ? {
              anticipation_of_purchase: data.context.anticipationOfPurchase,
            }
            : {}),
          ...(data.context.currencyCode
            ? {
              currency_code: data.context.currencyCode,
            }
            : {}),
          ...(data.context.previousSeats
            ? {
              previous_seats: data.context.previousSeats,
            }
            : {}),                                          
        },
      }
      : {}),
    ...(data.extras
      ? {
        extras: data.extras,
      }
      : {}),
  };
};

/**
 * Validates context data and inject into datalayer method
 * @typedef {Function} context
 * @param  {Object} [data] - context data overrides.
 * @param  {Function} [dataLayer] - context tracking datalayer method
 * @param  {Function} [callback] - Callback to fire after context call completes
 * @return {Void}
 * @api private
 */
const context = (data, dataLayer, callback) => {
  return validate(schemas.context, data)
    .then(() => {
      const eventName = "dataInit";
      const payload = buildContextPayload(data);

      // Reset GTM context for GA4 to prevent persistance across contexts on SPA
      resetDataLayer("context");

      dataLayer({
        event: eventName,
        ...payload,
        libVersion: VERSION, // VERSION will be injected by webpack globally.
        timestamp: timeStamp(),
        ...withCallbacks(callback),
      });

      existsGTM(callback);
    })
    .catch((error) => reportError(error));
};

export default context;
